import { emitAdminMutation, isAdminMutation } from './adminMutations';
import { ApiError, type ApiErrorBody } from './types';

/**
 * F06 — admin API fetch wrapper.
 *
 * Responsibilities:
 *   1. Resolve the API origin. In dev we use a same-origin base (`''`) so every
 *      call goes through the Vite dev proxy (see apps/admin/vite.config.ts),
 *      which keeps the HttpOnly `admin_refresh` cookie first-party and lets the
 *      session survive a page reload. In prod we prepend `VITE_API_ORIGIN`.
 *   2. Attach `Authorization: Bearer ${accessToken}` from the auth provider's
 *      live getter (set via `setAccessTokenGetter`).
 *   3. Always send `credentials: 'include'` so the `admin_refresh` cookie flows.
 *   4. On a 401 with `TOKEN_EXPIRED` / `TOKEN_INVALID`, call the registered
 *      refresh handler once, then retry the request with the fresh token.
 *      If refresh itself fails, the auth provider's logout handler is invoked
 *      and the original ApiError is rethrown.
 *   5. Parse JSON; throw `ApiError` for any non-2xx response.
 *
 * The module-level closure is intentional: AuthContext mounts/unmounts during
 * the app lifecycle, but the apiClient is imported once at module load. To
 * keep tokens fresh without prop-drilling, AuthProvider registers a getter
 * and a refresh handler at mount via `setAccessTokenGetter` / `setAuthHandlers`.
 */

// Dev: same-origin ('') so requests hit the SPA's own origin and the Vite dev
// proxy forwards them to the API — the `admin_refresh` cookie stays first-party,
// so it's delivered on the cold-start /admin/auth/refresh after a reload
// regardless of which host the SPA is opened on (localhost / 127.0.0.1 / LAN IP).
// Using an absolute cross-origin URL here (the old behaviour) made the cookie
// cross-site whenever SPA-host ≠ API-host, so `SameSite=Strict` withheld it and
// the admin was bounced to /login on every reload. Prod (admin + API on
// different subdomains) keeps the absolute origin from VITE_API_ORIGIN.
const API_ORIGIN: string = import.meta.env.DEV
  ? ''
  : ((import.meta.env.VITE_API_ORIGIN as string | undefined) ??
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
    '');

type TokenGetter = () => string | null;

interface AuthHandlers {
  readonly refresh: () => Promise<void>;
  readonly onAuthFailure: () => void;
}

let tokenGetter: TokenGetter = () => null;
let authHandlers: AuthHandlers | null = null;
let refreshInFlight: Promise<void> | null = null;

/** Used by AuthProvider on mount. */
export function setAccessTokenGetter(getter: TokenGetter): void {
  tokenGetter = getter;
}

/** Used by AuthProvider on mount. */
export function setAuthHandlers(handlers: AuthHandlers): void {
  authHandlers = handlers;
}

export interface AdminFetchInit extends Omit<RequestInit, 'body'> {
  /** Pre-stringified body or an object to JSON-encode. */
  readonly body?: BodyInit | Record<string, unknown> | null;
  /** Internal — prevents infinite refresh loops. */
  readonly _retry?: boolean;
}

function buildHeaders(init: AdminFetchInit | undefined): Headers {
  const headers = new Headers(init?.headers ?? {});
  const token = tokenGetter();
  if (token && !headers.has('authorization')) {
    headers.set('authorization', `Bearer ${token}`);
  }
  // Auto JSON content-type when body is a plain object.
  if (
    init?.body !== undefined &&
    init.body !== null &&
    !(init.body instanceof FormData) &&
    !(init.body instanceof URLSearchParams) &&
    !(init.body instanceof Blob) &&
    !(init.body instanceof ArrayBuffer) &&
    typeof init.body === 'object' &&
    !headers.has('content-type')
  ) {
    headers.set('content-type', 'application/json');
  }
  return headers;
}

function serialiseBody(body: AdminFetchInit['body']): BodyInit | null | undefined {
  if (body === null || body === undefined) return body as null | undefined;
  if (typeof body === 'string') return body;
  if (
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof Blob ||
    body instanceof ArrayBuffer
  ) {
    return body;
  }
  return JSON.stringify(body);
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return null;
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('application/json')) {
    // Empty body or non-JSON; return null for downstream consumers.
    const text = await res.text();
    return text ? text : null;
  }
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function isRetryableUnauthorized(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const code = (body as ApiErrorBody).error?.code;
  return code === 'TOKEN_EXPIRED' || code === 'TOKEN_INVALID';
}

async function doFetch(path: string, init: AdminFetchInit | undefined): Promise<Response> {
  const url =
    path.startsWith('http://') || path.startsWith('https://') ? path : `${API_ORIGIN}${path}`;
  // Strip the wide `body` (which may be a plain object) and `_retry` before
  // forwarding to the platform `fetch`; serialiseBody handles the conversion.
  const { body: _omitBody, _retry: _omitRetry, ...rest } = init ?? {};
  void _omitBody;
  void _omitRetry;
  const body = serialiseBody(init?.body);
  const requestInit: RequestInit = {
    ...rest,
    headers: buildHeaders(init),
    credentials: init?.credentials ?? 'include',
  };
  if (body !== undefined) {
    requestInit.body = body;
  }
  return fetch(url, requestInit);
}

/**
 * Decide whether a failed response should trigger the single refresh + retry.
 * When it should, runs the (single-flighted) refresh and resolves `true`; when
 * the refresh itself fails, invokes `onAuthFailure` and throws the original
 * `ApiError`. Resolves `false` when no retry applies.
 */
async function refreshForRetry(
  res: Response,
  body: ApiErrorBody | null,
  init: AdminFetchInit | undefined,
): Promise<boolean> {
  if (res.status !== 401 || init?._retry || !authHandlers || !isRetryableUnauthorized(body)) {
    return false;
  }
  const handlers = authHandlers;
  try {
    if (!refreshInFlight) {
      refreshInFlight = handlers.refresh().finally(() => {
        refreshInFlight = null;
      });
    }
    await refreshInFlight;
  } catch {
    handlers.onAuthFailure();
    throw new ApiError(res.status, body);
  }
  return true;
}

/**
 * Issue an authenticated request and return the parsed JSON body.
 *
 * @throws {ApiError} on any non-2xx response.
 */
export async function adminFetch<T = unknown>(path: string, init?: AdminFetchInit): Promise<T> {
  const res = await doFetch(path, init);
  if (res.ok) {
    const parsed = (await parseBody(res)) as T;
    // F60b — let the sidebar badges refresh after an admin action.
    if (isAdminMutation(init?.method, path)) emitAdminMutation(path);
    return parsed;
  }
  const body = (await parseBody(res)) as ApiErrorBody | null;
  // Token expiry / invalid → try a single refresh + retry.
  if (await refreshForRetry(res, body, init)) {
    return adminFetch<T>(path, { ...init, _retry: true });
  }
  throw new ApiError(res.status, body);
}

/** Raw bytes returned by `adminFetchBlob`. */
export interface AdminBlobResponse {
  readonly blob: Blob;
  /** The response `Content-Type` (falls back to the blob's own type). */
  readonly contentType: string;
}

/**
 * F09 v2 (E7) — authenticated binary fetch. Same bearer + cookie + single
 * refresh-and-retry behaviour as `adminFetch`, but returns the body as a Blob
 * (for the verification-document proxy, rendered through a blob object URL).
 * Never caches: the proxy logs every view server-side.
 *
 * @throws {ApiError} on any non-2xx response (the error body is parsed as JSON).
 */
export async function adminFetchBlob(
  path: string,
  init?: AdminFetchInit,
): Promise<AdminBlobResponse> {
  const res = await doFetch(path, { cache: 'no-store', ...init });
  if (res.ok) {
    const blob = await res.blob();
    const contentType =
      res.headers.get('content-type') ?? (blob.type || 'application/octet-stream');
    return { blob, contentType };
  }
  const body = (await parseBody(res)) as ApiErrorBody | null;
  if (await refreshForRetry(res, body, init)) {
    return adminFetchBlob(path, { ...init, _retry: true });
  }
  throw new ApiError(res.status, body);
}

/** Construct a query string from a record of primitives, omitting nullish values. */
export function buildQuery(
  params: Record<string, string | number | boolean | undefined | null>,
): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    usp.set(key, String(value));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export const API_BASE = API_ORIGIN;
